<template>
  <div>
    <b-row class="align-items-end mb-4">
      <b-col cols="12" md="3">
        <b-form-group label="Período" label-for="periodo">
          <b-form-select v-model="form.periodo" :options="periodoOptions" @change="onPeriodoChange" />
        </b-form-group>
      </b-col>
      <template v-if="form.periodo === 'personalizado'">
        <b-col cols="12" md="2">
          <b-form-group label="Data inicial" label-for="startDate">
            <b-form-input type="date" v-model="form.customStart" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="Data final" label-for="endDate">
            <b-form-input type="date" v-model="form.customEnd" />
          </b-form-group>
        </b-col>
      </template>
      <b-col cols="12" md="2">
        <b-form-group label="Visão" label-for="visao">
          <b-form-select v-model="form.visao" :options="visaoOptions" />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="2">
        <b-form-group label-for="regime">
          <template #label>
            Regime
            <b-icon
              id="tooltip-regime"
              icon="info-circle"
              class="ml-1 text-muted"
              style="cursor: pointer; font-size: 0.85rem;"
            />
            <b-tooltip target="tooltip-regime" triggers="hover" placement="top">
              <div class="text-left" style="font-size: 0.8rem;">
                <p class="mb-1"><strong>Competência:</strong> considera receitas e despesas na data em que foram geradas, independente do que foi recebido/pago.</p>
                <p class="mb-0"><strong>Caixa:</strong> considera apenas o que foi efetivamente recebido/pago.</p>
              </div>
            </b-tooltip>
          </template>
          <b-form-select v-model="form.regime" :options="regimeOptions" />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="3">
        <b-form-group label=" " label-for="btn-gerar">
          <b-button variant="success" @click="fetchData">Gerar relatório</b-button>
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p>Carregando...</p>
    </div>

    <div v-else-if="report" class="card shadow-sm" id="dre-print-area">
      <div class="card-body p-4">
        <div class="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h5 class="mb-1 font-weight-bold">DRE gerencial</h5>
            <small class="text-muted">{{ reportSubtitle }}</small>
          </div>
          <div>
            <b-button variant="outline-secondary" size="sm" class="mr-2" @click="exportar">
              <b-icon icon="download" /> Exportar
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="imprimir">
              <b-icon icon="printer" /> Imprimir
            </b-button>
          </div>
        </div>
        <RelatorioDREGerencialTable :linhas="report.linhas" :periods="report.periods" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import RelatorioDREGerencialTable from "./componentes/RelatorioDREGerencialTable.vue";

export default {
  name: "app-relatorio-dre",
  components: { RelatorioDREGerencialTable },
  data() {
    return {
      isLoading: false,
      form: {
        periodo: "este_ano",
        customStart: moment().startOf("year").format("YYYY-MM-DD"),
        customEnd: moment().endOf("year").format("YYYY-MM-DD"),
        visao: "mensal",
        regime: "caixa",
      },
      report: null,
    };
  },
  computed: {
    periodoOptions() {
      return [
        { value: "este_ano", text: "Este ano" },
        { value: "esta_semana", text: "Esta semana" },
        { value: "este_mes", text: "Este mês" },
        { value: "mes_passado", text: "Mês passado" },
        { value: "este_trimestre", text: "Este trimestre" },
        { value: "este_semestre", text: "Este semestre" },
        { value: "personalizado", text: "Período personalizado" },
      ];
    },
    visaoOptions() {
      return [
        { value: "mensal", text: "Mensal" },
        { value: "trimestral", text: "Trimestral" },
        { value: "semestral", text: "Semestral" },
        { value: "anual", text: "Anual" },
      ];
    },
    regimeOptions() {
      return [
        { value: "competencia", text: "Competência" },
        { value: "caixa", text: "Caixa" },
      ];
    },
    startDate() {
      return this.resolvePeriod().start;
    },
    endDate() {
      return this.resolvePeriod().end;
    },
    reportSubtitle() {
      const startFmt = moment(this.startDate).format("DD/MM/YYYY");
      const endFmt = moment(this.endDate).format("DD/MM/YYYY");
      const visaoLabel = this.visaoOptions.find((v) => v.value === this.form.visao)?.text || "";
      const regimeLabel = this.regimeOptions.find((r) => r.value === this.form.regime)?.text || "";
      return `De ${startFmt} à ${endFmt} · Visão ${visaoLabel} · Regime ${regimeLabel}`;
    },
  },
  methods: {
    resolvePeriod() {
      const now = moment();
      const fmt = "YYYY-MM-DD";
      switch (this.form.periodo) {
        case "esta_semana":
          return { start: now.clone().startOf("isoWeek").format(fmt), end: now.clone().endOf("isoWeek").format(fmt) };
        case "este_mes":
          return { start: now.clone().startOf("month").format(fmt), end: now.clone().endOf("month").format(fmt) };
        case "mes_passado":
          return { start: now.clone().subtract(1, "month").startOf("month").format(fmt), end: now.clone().subtract(1, "month").endOf("month").format(fmt) };
        case "este_trimestre":
          return { start: now.clone().startOf("quarter").format(fmt), end: now.clone().endOf("quarter").format(fmt) };
        case "este_semestre": {
          const semStart = now.month() < 6 ? now.clone().month(0).startOf("month") : now.clone().month(6).startOf("month");
          const semEnd = now.month() < 6 ? now.clone().month(5).endOf("month") : now.clone().month(11).endOf("month");
          return { start: semStart.format(fmt), end: semEnd.format(fmt) };
        }
        case "personalizado":
          return { start: this.form.customStart, end: this.form.customEnd };
        case "este_ano":
        default:
          return { start: now.clone().startOf("year").format(fmt), end: now.clone().endOf("year").format(fmt) };
      }
    },
    onPeriodoChange(value) {
      if (value !== "personalizado") return;
      this.form.customStart = moment().startOf("month").format("YYYY-MM-DD");
      this.form.customEnd = moment().endOf("month").format("YYYY-MM-DD");
    },
    async fetchData() {
      this.isLoading = true;
      this.report = null;
      try {
        const payload = {
          startDate: this.startDate,
          endDate: this.endDate,
          visao: this.form.visao,
        };
        if (this.form.regime) payload.regime = this.form.regime;
        const response = await this.$http.post("/financeiro/dre", payload);
        this.report = response.data;
      } catch (error) {
        this.$toast.error("Erro ao buscar os dados do relatório DRE.");
      } finally {
        this.isLoading = false;
      }
    },
    exportar() {
      const { linhas, periods } = this.report;

      const toCurrency = (value) =>
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
        }).format(value || 0);

      const sep = ";";
      const header = ["Categoria", ...periods, "Total"].join(sep);
      const rows = [header];

      linhas.forEach((linha) => {
        if (linha.tipo === "secao") {
          const cols = [
            `"${linha.descricao}"`,
            ...periods.map((p) => {
              const b = (linha.balances || []).find((x) => x.period === p);
              return toCurrency(b ? b.balance : 0);
            }),
            toCurrency(linha.total),
          ];
          rows.push(cols.join(sep));

          (linha.planos || []).forEach((plano) => {
            const planoCols = [
              `"  ${plano.descricao}"`,
              ...periods.map((p) => {
                const b = (plano.balances || []).find((x) => x.period === p);
                return toCurrency(b ? b.balance : 0);
              }),
              toCurrency(
                (plano.balances || []).reduce((s, b) => s + (b.balance || 0), 0)
              ),
            ];
            rows.push(planoCols.join(sep));
          });
        } else if (linha.tipo === "subtotal") {
          const cols = [
            `"${linha.descricao}"`,
            ...periods.map((p) => {
              const b = (linha.balances || []).find((x) => x.period === p);
              return toCurrency(b ? b.balance : 0);
            }),
            toCurrency(linha.total),
          ];
          rows.push(cols.join(sep));
          rows.push("");
        }
      });

      const bom = "\uFEFF";
      const blob = new Blob([bom + rows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `DRE_Gerencial_${this.startDate}_${this.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    imprimir() {
      const conteudo = document.getElementById("dre-print-area").innerHTML;
      const janela = window.open("", "_blank");
      janela.document.write(`
        <html>
          <head>
            <title>DRE Gerencial</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css">
            <style>
              body { font-family: sans-serif; padding: 20px; font-size: 0.9rem; }
              table { width: 100%; border-collapse: collapse; }
              th, td { padding: 0.4rem 0.75rem; border: 1px solid #dee2e6; font-size: 0.88rem; }
              .text-right { text-align: right; }
              .text-success { color: #28a745 !important; }
              .text-danger { color: #dc3545 !important; }
              .row-secao td { background-color: #f8f9fa; font-weight: bold; }
              .row-subtotal td { background-color: #f0f4f8; font-weight: bold; }
              .plano-descricao { padding-left: 2.5rem !important; }
              button, .btn { display: none !important; }
            </style>
          </head>
          <body>${conteudo}</body>
        </html>
      `);
      janela.document.close();
      janela.focus();
      janela.print();
      janela.close();
    },
  },
};
</script>
